import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { NotificationCode, NotificationSeverity } from '../models/communication/app-enums.model';

export interface AppNotification {
    id: string;
    code?: NotificationCode;
    title?: string;
    description?: string;
    severity: NotificationSeverity;
    showCloseButton: boolean;
    context?: unknown;
    timestamp: number;
}

export interface NotificationOptions {
    code?: NotificationCode;
    title?: string;
    description?: string;
    severity: NotificationSeverity;
    showCloseButton?: boolean;
    context?: unknown;
    duration?: number;
    persistent?: boolean;
    deduplicate?: boolean;
}

/**
 * Service responsible for managing the display of notifications in the application.
 * Supports multiple simultaneous notifications with configurable options.
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationService implements OnDestroy {
    private static readonly defaultDurationMs = 3000;
    private static readonly maxNotifications = 5;

    private static readonly radixBase = 36;
    private static readonly randomStringStart = 2;
    private static readonly randomStringEnd = 11;

    /**
     * Observable stream of all active notifications
     */
    readonly notifications$: Observable<AppNotification[]>;

    private readonly notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
    private readonly timeoutMap = new Map<string, ReturnType<typeof setTimeout>>();

    constructor() {
        this.notifications$ = this.notificationsSubject.asObservable();
    }

    get notifications(): AppNotification[] {
        return this.notificationsSubject.value;
    }

    /**
     * Displays a notification with the specified parameters.
     * Returns the notification ID for later reference.
     */
    show(options: NotificationOptions): string {
        if (options.deduplicate) {
            const existingId = this.findDuplicate(options);
            if (existingId) {
                this.refreshNotification(existingId, options);
                return existingId;
            }
        }

        const notification = this.createNotification(options);
        this.addNotification(notification);
        this.setupAutoDismiss(notification, options);

        return notification.id;
    }

    /**
     * Dismisses a specific notification by ID
     */
    dismiss(id: string): void {
        this.clearTimeout(id);

        const filtered = this.notifications.filter((n) => n.id !== id);
        this.notificationsSubject.next(filtered);
    }

    /**
     * Dismisses all notifications
     */
    dismissAll(): void {
        this.timeoutMap.forEach((timeoutId) => clearTimeout(timeoutId));
        this.timeoutMap.clear();

        this.notificationsSubject.next([]);
    }

    /**
     * Remove all notifications with a specific code
     */
    dismissByCode(code: NotificationCode): void {
        const toRemove = this.notifications.filter((n) => n.code === code);
        toRemove.forEach((n) => this.dismiss(n.id));
    }

    /**
     * Update an existing notification
     */
    update(id: string, updates: Partial<Omit<AppNotification, 'id'>>): void {
        const notifications = this.notifications.map((n) => (n.id === id ? { ...n, ...updates } : n));
        this.notificationsSubject.next(notifications);
    }

    /**
     * Show or update a notification based on code
     */
    showOrUpdate(options: NotificationOptions & { code: NotificationCode }): string {
        const existing = this.notifications.find((n) => n.code === options.code);

        if (existing) {
            this.update(existing.id, {
                title: options.title,
                description: options.description,
                severity: options.severity,
                showCloseButton: options.showCloseButton ?? false,
                context: options.context
            });
            return existing.id;
        }

        return this.show(options);
    }

    ngOnDestroy(): void {
        this.dismissAll();
    }

    // Private helper methods
    private createNotification(options: NotificationOptions): AppNotification {
        return {
            id: this.generateId(),
            code: options.code,
            title: options.title,
            description: options.description,
            severity: options.severity,
            showCloseButton: options.showCloseButton ?? false,
            context: options.context,
            timestamp: Date.now()
        };
    }

    private addNotification(notification: AppNotification): void {
        const currentNotifications = [...this.notifications, notification];

        // Remove oldest if exceeding max
        if (currentNotifications.length > NotificationService.maxNotifications) {
            const removed = currentNotifications.shift();
            if (removed) {
                this.clearTimeout(removed.id);
            }
        }

        this.notificationsSubject.next(currentNotifications);
    }

    private setupAutoDismiss(notification: AppNotification, options: NotificationOptions): void {
        if (!options.persistent && !notification.showCloseButton) {
            const duration = options.duration ?? NotificationService.defaultDurationMs;
            const timeoutId = setTimeout(() => this.dismiss(notification.id), duration);
            this.timeoutMap.set(notification.id, timeoutId);
        }
    }

    private findDuplicate(options: NotificationOptions): string | null {
        const duplicate = this.notifications.find(
            (n) =>
                n.code === options.code &&
                n.title === options.title &&
                n.description === options.description &&
                n.severity === options.severity
        );

        return duplicate?.id ?? null;
    }

    private refreshNotification(id: string, options: NotificationOptions): void {
        // Update timestamp (and context, which may have changed) to refresh the notification
        this.update(id, { timestamp: Date.now(), context: options.context });

        // Reset auto-dismiss timer if applicable
        if (!options.persistent && !options.showCloseButton) {
            this.clearTimeout(id);
            const duration = options.duration ?? NotificationService.defaultDurationMs;
            const timeoutId = setTimeout(() => this.dismiss(id), duration);
            this.timeoutMap.set(id, timeoutId);
        }
    }

    private clearTimeout(id: string): void {
        const timeoutId = this.timeoutMap.get(id);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeoutMap.delete(id);
        }
    }

    private generateId(): string {
        return `notification-${Date.now()}-${Math.random().toString(NotificationService.radixBase).substring(NotificationService.randomStringStart, NotificationService.randomStringEnd)}`;
    }
}
